/**
 * Icon component — shared types and Zod prop schema.
 *
 * The name prop is constrained to IconName, ensuring only icons
 * registered in the design system can be used in application code.
 *
 * SVG path data and type definitions live in elements/icons/.
 */

import { z } from 'zod';
import { iconNames } from '../../elements/icons/registry';
import { colors, iconToneNames } from '../../elements/colors';

// Re-export icon data so platform renderers can import from this file.

export const IconNameEnum = z.enum(iconNames);

/**
 * Semantic tint from the `colors.icon` token group. The token-safe way for
 * GenUI documents (pure data, no token imports) to colour an icon; when both
 * are supplied, `tone` wins over the raw `color` string.
 */
export const IconTone = z.enum(iconToneNames);
export type IconTone = z.infer<typeof IconTone>;

export const iconPropsSchema = z.object({
  name: IconNameEnum,
  size: z.number().positive().default(24),
  color: z.string().default(colors.icon.primary),
  tone: IconTone.optional(),
});

export type IconProps = z.infer<typeof iconPropsSchema>;

/** Default icon size in pixels. */
export const DEFAULT_ICON_SIZE = 24;

/** Default icon color — uses the design system icon.primary token. */
export const DEFAULT_ICON_COLOR = colors.icon.primary;

/** Resolve the effective icon colour: semantic tone first, then the raw color. */
export function iconColorFor(props: { color?: string; tone?: IconTone }): string {
  if (props.tone) return colors.icon[props.tone];
  return props.color ?? DEFAULT_ICON_COLOR;
}
